import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import { LeadChangeHistoryViewModel } from "../../../../api";
import { LeadsService } from "../../../../api/services/LeadsService";
import { HelpersService } from "../../../../services/Helpers";
import { StatusChip } from "../../../../components/ui";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(relativeTime);

const MySwal = withReactContent(Swal);

interface ChangeHistoryDrawerProps {
  leadId?: number | null;
  open: boolean;
  onClose: () => void;
}

type ChangeGroup = {
  key: string;
  changedAt: string;
  changedByName: string;
  changes: LeadChangeHistoryViewModel[];
};

function formatFieldName(fieldName?: string | null) {
  if (!fieldName) {
    return "";
  }
  return HelpersService.camelToString(fieldName, false) ?? fieldName;
}

function formatSection(section?: string | null) {
  if (!section) {
    return "Lead";
  }
  if (section === "PersonalInjury") {
    return "Personal Injury";
  }
  if (section === "WorkersCompensation") {
    return "Workers Compensation";
  }
  if (section === "ClassAction") {
    return "Class Action";
  }
  return HelpersService.camelToString(section, false) || section;
}

function formatDisplayValue(value?: string | null): string {
  if (value == null || value === "") {
    return "Empty";
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const timeZone = dayjs.tz.guess();
    return dayjs.utc(value).tz(timeZone).format("MMM D, YYYY h:mm A");
  }
  if (value === "true") {
    return "Yes";
  }
  if (value === "false") {
    return "No";
  }
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.length === 0 ? "Empty" : parsed.join(", ");
    }
  } catch {
    // not JSON
  }
  return value;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function DiffValue({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "old" | "new";
}) {
  const isEmpty = value === "Empty";
  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.disabled",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: "0.65rem",
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        title={value}
        sx={{
          color: isEmpty
            ? "text.disabled"
            : tone === "old"
              ? "text.secondary"
              : "text.primary",
          textDecoration: tone === "old" && !isEmpty ? "line-through" : "none",
          fontWeight: tone === "new" && !isEmpty ? 600 : 400,
          fontStyle: isEmpty ? "italic" : "normal",
          wordBreak: "break-word",
          lineHeight: 1.4,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function ChangeHistoryDrawer({
  leadId,
  open,
  onClose,
}: ChangeHistoryDrawerProps) {
  const [history, setHistory] = useState<LeadChangeHistoryViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open || !leadId) {
      return;
    }
    setLoading(true);
    LeadsService.getApiLeadsChangeHistory(leadId)
      .then((rows) => setHistory(rows ?? []))
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error loading change history</p>,
        });
      })
      .finally(() => setLoading(false));
  }, [open, leadId]);

  const grouped = useMemo(() => {
    const groups: ChangeGroup[] = [];
    const query = search.trim().toLowerCase();

    const filtered = !query
      ? history
      : history.filter((row) => {
          const haystack = [
            row.changedByName,
            row.section,
            row.fieldName,
            row.oldValue,
            row.newValue,
            formatFieldName(row.fieldName),
            formatSection(row.section),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(query);
        });

    for (const row of filtered) {
      const key = `${row.changedAt}|${row.changedBy}`;
      const existing = groups.find((g) => g.key === key);
      if (existing) {
        existing.changes.push(row);
      } else {
        groups.push({
          key,
          changedAt: row.changedAt ?? "",
          changedByName: row.changedByName ?? `User ${row.changedBy}`,
          changes: [row],
        });
      }
    }
    return groups;
  }, [history, search]);

  const sessionCount = useMemo(() => {
    return new Set(history.map((h) => `${h.changedAt}|${h.changedBy}`)).size;
  }, [history]);

  const toggleGroup = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const timeZone = dayjs.tz.guess();
  const totalChanges = history.length;
  const hasSearch = search.trim().length > 0;
  const summaryText = loading
    ? "Loading changes…"
    : totalChanges === 0
      ? "No edits recorded yet"
      : hasSearch
        ? `${grouped.reduce((n, g) => n + g.changes.length, 0)} matching change${grouped.reduce((n, g) => n + g.changes.length, 0) === 1 ? "" : "s"}`
        : `${totalChanges} field change${totalChanges === 1 ? "" : "s"} across ${sessionCount} save${sessionCount === 1 ? "" : "s"}`;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560, md: 640 },
          bgcolor: "#f7f8fa",
        },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "#fff",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 2.5,
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 40,
              height: 40,
              mt: 0.25,
            }}
          >
            <HistoryIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Intake change history
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {summaryText}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close history" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Search by field, value, or person…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ px: 2.5, py: 2.5 }}>
        {loading && (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
            Loading history…
          </Typography>
        )}

        {!loading && totalChanges === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 3,
              bgcolor: "#fff",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <HistoryIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              No history yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Field-level changes will appear here when someone edits and saves
              this intake form.
            </Typography>
          </Box>
        )}

        {!loading && totalChanges > 0 && grouped.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
            No changes match “{search}”.
          </Typography>
        )}

        {!loading &&
          grouped.map((group, groupIndex) => {
            const absolute = dayjs
              .utc(group.changedAt)
              .tz(timeZone)
              .format("MMM D, YYYY · h:mm A");
            const relative = dayjs.utc(group.changedAt).tz(timeZone).fromNow();
            const isCollapsed = !!collapsed[group.key];
            const sections = Array.from(
              new Set(group.changes.map((c) => formatSection(c.section)))
            );

            const bySection = sections.map((section) => ({
              section,
              items: group.changes.filter(
                (c) => formatSection(c.section) === section
              ),
            }));

            return (
              <Box
                key={group.key}
                sx={{
                  position: "relative",
                  pl: 3.5,
                  pb: groupIndex === grouped.length - 1 ? 0 : 2.5,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 15,
                    top: 36,
                    bottom: 0,
                    width: 2,
                    bgcolor: groupIndex === grouped.length - 1 ? "transparent" : "divider",
                  },
                }}
              >
                <Avatar
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 12,
                    width: 32,
                    height: 32,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                  }}
                >
                  {getInitials(group.changedByName)}
                </Avatar>

                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
                  }}
                >
                  <Box
                    onClick={() => toggleGroup(group.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, lineHeight: 1.3 }}
                      >
                        {group.changedByName}
                      </Typography>
                      <Tooltip title={absolute}>
                        <Typography variant="caption" color="text.secondary">
                          {relative} · {absolute}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <StatusChip
                      size="small"
                      label={`${group.changes.length} field${group.changes.length === 1 ? "" : "s"}`}
                      sx={{ fontWeight: 600 }}
                    />
                    <IconButton size="small">
                      {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                  </Box>

                  {!isCollapsed && (
                    <>
                      <Divider />
                      <Box sx={{ px: 2, py: 1.5 }}>
                        {bySection.map((sectionGroup, sectionIndex) => (
                          <Box
                            key={sectionGroup.section}
                            sx={{ mb: sectionIndex === bySection.length - 1 ? 0 : 2 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "inline-block",
                                mb: 1,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: "grey.100",
                                color: "text.secondary",
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                              }}
                            >
                              {sectionGroup.section}
                            </Typography>

                            {sectionGroup.items.map((change, itemIndex) => (
                              <Box
                                key={change.id}
                                sx={{
                                  py: 1.25,
                                  borderTop:
                                    itemIndex === 0 ? "none" : "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, mb: 0.75 }}
                                >
                                  {formatFieldName(change.fieldName)}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <DiffValue
                                    label="Before"
                                    value={formatDisplayValue(change.oldValue)}
                                    tone="old"
                                  />
                                  <Typography
                                    color="text.disabled"
                                    sx={{ mt: 2.2, fontWeight: 700 }}
                                  >
                                    →
                                  </Typography>
                                  <DiffValue
                                    label="After"
                                    value={formatDisplayValue(change.newValue)}
                                    tone="new"
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
      </Box>
    </Drawer>
  );
}
